import { BreakpointObserver } from '@angular/cdk/layout';
import { DOCUMENT, Location, NgClass } from '@angular/common';
import { ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, inject, ViewChild } from '@angular/core';
import { MatDrawer, MatDrawerContainer, MatDrawerContent } from '@angular/material/sidenav';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { ThemeModule } from '@dxc-technology/halstack-angular';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';
import { CommonService, setLocalStorageItem } from 'src/app/services/common.service';
import { environment } from 'src/environments/environment';
import { CollapseSideNavigationComponent } from './shared/components/layout/collapse-side-navigation/collapse-side-navigation.component';
import { FooterComponent } from './shared/components/layout/footer/footer.component';
import { HeaderComponent } from './shared/components/layout/header/header.component';
import { LoadingComponent } from './shared/components/UI/loading/loading.component';
import { User } from '@shared/types/rbac';
import { OAuthService } from 'angular-oauth2-oidc';
import _ from 'lodash';
import { CookieService } from 'ngx-cookie-service';
import { LoginService } from './services/login.service';
import { ProposalService } from './services/proposal.service';
import { RbacService } from './services/rbac.service';
import { SpakFormService } from './services/spak-form.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    LoadingComponent,
    ThemeModule,
    MatDrawerContainer,
    MatDrawer,
    CollapseSideNavigationComponent,
    NgClass,
    MatDrawerContent,
    HeaderComponent,
    RouterOutlet,
    FooterComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppComponent {
  private readonly document = inject(DOCUMENT);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly rbacService = inject(RbacService);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  protected commonService = inject(CommonService);
  private readonly proposalService = inject(ProposalService);
  private readonly spakService = inject(SpakFormService);
  private readonly breakpointObserver = inject(BreakpointObserver); // Inject BreakpointObserver
  @ViewChild('sideNavComp') sideNavComp!: CollapseSideNavigationComponent;
  private readonly oauthService = inject(OAuthService);
  private readonly location = inject(Location);
  private readonly loginService = inject(LoginService);
  private readonly cookieService = inject(CookieService);
  private viewInitialized = false;
  private pendingNavigation: { auth: any; isFirstTimeLogin: boolean } | null = null;

  title = 'PRU-Quotation-UI-Portal';

  RESOURCE_SERVER_URL = environment.RESOURCE_SERVER_URL;
  isMobile: boolean = false;
  isMenuOpend: boolean = true;
  hideFooter: boolean = environment.hideFooter;
  navigationMenu = [
    //Quote and Apply
    {
      id: 1,
      name: 'navigationBar.dashboard',
      path: 'proposal',
      icon: this.RESOURCE_SERVER_URL + '/assets/images/side_menu/Quote_and_Apply.svg',
      iconSelected: this.RESOURCE_SERVER_URL + '/assets/images/side_menu/Quote_and_Apply_Select.svg',
      isSelected: false,
      inquire: true,
    },
    //Registration
    {
      id: 2,
      name: 'navigationBar.registration',
      path: 'registration',
      icon: this.RESOURCE_SERVER_URL + '/assets/images/side_menu/Registration.svg',
      iconSelected: this.RESOURCE_SERVER_URL + '/assets/images/side_menu/Registration_Select.svg',
      isSelected: false,
      inquire: true,
    },
  ];

  constructor(private cdr: ChangeDetectorRef) {
    this.idpConfiguration = environment.IDP;
    this.configureSingleSignOn();
    //* To show loading indicator when access route which has a lazy-loaded component
    this.router.events.pipe(filter((event) => event instanceof NavigationStart || event instanceof NavigationEnd)).subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.commonService.setLoading(true);
      } else if (event instanceof NavigationEnd) {
        this.commonService.setLoading(false);
        requestAnimationFrame(() => this.scrollToTop());
      }
    });

    if (this.PROFILE === environment.ON_CUSTOMIDP) {
      window.addEventListener('customidp-auth-complete', () => {
        this.initializeCustomIDPAfterAuth();
      });
    }

    const translate = this.translate;

    translate.addLangs(['en', 'cy']);
    translate.setDefaultLang('en');

    const browserLang = translate.getBrowserLang();
    translate.use(browserLang.match(/en|cy/) ? browserLang : 'en');
  }

  ON_PREMISES_CAS = environment.ON_PREMISES_CAS;
  ON_PREMISES = environment.ON_PREMISES;
  ON_CLOUD = environment.ON_CLOUD;
  PROFILE = environment.ENV_ACTIVE_PROFILE;
  isAuthorized: boolean = false;
  activatedURL: any;
  loggedIn: boolean;
  hasIdtoken: any;
  hasAccessToken: any;
  idpConfiguration;
  accessToken = '';
  userId: any;

  private scrollToTop() {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    this.document.documentElement.scrollTop = 0;
    this.document.body.scrollTop = 0;
    const scrollContainers = this.document.querySelectorAll<HTMLElement>('.app-content, .mat-drawer-content, #main-mat-drawer-container');
    scrollContainers.forEach((container) => {
      container.scrollTop = 0;
      container.scrollLeft = 0;
    });
  }

  async ngOnInit() {
    // Breakpoint Observer logic
    this.breakpointObserver.observe(['(max-width: 1024px)']).subscribe((result) => {
      this.isMobile = result.matches;
      if (this.isMobile) {
        this.isMenuOpend = false; // Always close menu when switching to mobile
      } else {
        this.isMenuOpend = true; // Open menu on desktop by default
      }
    });

    if (localStorage.getItem('selectedSideNavigation') == null || localStorage.getItem('selectedSideNavigation') == 'null')
      localStorage.setItem('selectedSideNavigation', 'proposal');
    if (!environment.isLogin) {
      this.commonService.getCognitoLogin();
      this.setLocalStorage();
    } else {
      this.initLogin();
    }
    const groupSalesAct = localStorage.getItem('groupSalesAct');
    if (groupSalesAct) {
      const retrievedJsonObject = JSON.parse(groupSalesAct);
      this.handleNavigationMenu(retrievedJsonObject, false);
    }

    //Get Language Dropdown
    const token = localStorage.getItem('access_token');
    if (token) {
      await this.commonService.getAllConfigDropdown();
    }
    window.addEventListener('localStorageChanged', async (event: any) => {
      if (event.key === 'access_token' && event.newValue) {
        await this.commonService.getAllConfigDropdown(event.newValue);
      }
    });
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    if (this.pendingNavigation) {
      const { auth, isFirstTimeLogin } = this.pendingNavigation;
      this.pendingNavigation = null;
      this._handleNavigationMenu(auth, isFirstTimeLogin);
    }
    this.cdr.detectChanges();
  }

  handleNavigationMenu(auth: any, isFirstTimeLogin: boolean) {
    if (!this.viewInitialized) {
      this.pendingNavigation = { auth, isFirstTimeLogin };
    } else {
      this._handleNavigationMenu(auth, isFirstTimeLogin);
    }
  }

  private _handleNavigationMenu(auth: any, isFirstTimeLogin: boolean) {
    try {
      this.navigationMenu[0].inquire = auth?.proposal?.actions?.Inquire ?? true;
      this.navigationMenu[1].inquire = auth?.registration?.actions?.Inquire ?? true;
      this.sideNavComp.initHome(isFirstTimeLogin);
    } catch (error) {
      console.error('handleNavigationMenu error:', error);
    }
  }

  async environmentURLSetup() {
    if (this.ON_PREMISES === this.PROFILE) {
      // onPremises enviroment
      this.configureSingleSignOn();
    } else if (this.ON_CLOUD === this.PROFILE) {
      // Congito environment
    }
  }

  configureSingleSignOn() {
    this.oauthService.configure(this.idpConfiguration);
    this.oauthService.loadDiscoveryDocumentAndTryLogin();
  }

  loginScreen() {
    this.activatedURL = window.location.href;
    if (this.activatedURL.endsWith('/admin') || this.activatedURL.endsWith('/login')) {
      this.loggedIn = true;
    } else if (!this.activatedURL.endsWith('/login')) {
      this.loggedIn = false;
    }
  }

  async setLocalStorageWithAPI() {
    const isCustomIDPAgent = this.PROFILE === environment.ON_CUSTOMIDP && this.loginService.isAgentUser();

    if (isCustomIDPAgent) {
      localStorage.setItem('lobDropDown', JSON.stringify(['N']));
      const persona = sessionStorage.getItem('selected_role') || 'N_GS';
      sessionStorage.setItem('selected_role', persona);
      localStorage.setItem('personaDropDown', JSON.stringify([persona]));
      return;
    }

    const personaDropDown = await this.commonService.getProfileUser(localStorage.getItem('userName'));
    const lobDropDown = await this.commonService.getLOBdropdown(localStorage.getItem('userName'));
    localStorage.setItem('lobDropDown', JSON.stringify(lobDropDown.body.lob));
    localStorage.setItem('personaDropDown', JSON.stringify(personaDropDown.body.persona));
  }

  private processUserRoleData(userRoleRes: any) {
    if (userRoleRes['userName']) {
      setLocalStorageItem('profilename', userRoleRes['userName']);
      setLocalStorageItem('userdispName', userRoleRes['userName']);

      if (this.PROFILE !== environment.ON_CUSTOMIDP) {
        setLocalStorageItem('fullNameUser', userRoleRes['userName']);
      }
    }

    if (userRoleRes['userModelId']) {
      setLocalStorageItem('selectedUserId', userRoleRes['userModelId']);
    } else if (userRoleRes['userId']) {
      setLocalStorageItem('selectedUserId', userRoleRes['userId']);
    }

    if (userRoleRes['lob']) {
      setLocalStorageItem('lob', userRoleRes['lob']);
    }

    if (userRoleRes['personaLabel']) {
      setLocalStorageItem('personaLabel', userRoleRes['personaLabel']);
    }
  }

  private processProfileData(profiles: any, groupId: string, init: boolean) {
    if (!profiles || _.isEmpty(profiles)) {
      this.isAuthorized = false;
      return;
    }

    if (profiles.roleType) {
      sessionStorage.setItem('selected_role', profiles.roleType);
    } else if (this.PROFILE === environment.ON_CUSTOMIDP) {
      sessionStorage.setItem('selected_role', 'N_GS');
    }
    if (profiles.clientId) localStorage.setItem('clientNumber', profiles.clientId);
    if (profiles.country) {
      localStorage.setItem('country', profiles.country);
      sessionStorage.setItem('regionSelected', profiles.country);
    } else if (this.PROFILE === environment.ON_CUSTOMIDP) {
      localStorage.setItem('country', 'IDN');
      sessionStorage.setItem('regionSelected', 'IDN');
    }
    if (this.PROFILE === environment.ON_CUSTOMIDP && !localStorage.getItem('language')) {
      localStorage.setItem('language', environment.language);
    }
    if (profiles.uuid) localStorage.setItem('profileId', profiles.uuid);
    if (profiles.lob) localStorage.setItem('lob', profiles.lob);
    if (profiles.groupSalesAuthority) {
      localStorage.setItem('groupSalesAct', JSON.stringify(profiles.groupSalesAuthority));
      this.handleNavigationMenu(profiles.groupSalesAuthority, init);
    }
    this.spakService.updateEditFieldFromStorage();
    this.proposalService.updateEditFieldFromStorage();
    this.rbacService.setUser({
      ..._.omit(profiles, ['_links', '_options']),
      groupId,
    } as User);
  }

  async initParam(userName: string, init: boolean = true) {
    setLocalStorageItem('userId', userName);
    setLocalStorageItem('userName', userName);

    let userRoleRes;
    try {
      const isCustomIDPAgent = this.PROFILE === environment.ON_CUSTOMIDP && this.loginService.isAgentUser();

      if (isCustomIDPAgent) {
        userRoleRes = await this.loginService.getGroupSalesUserProfile(userName);
      } else {
        userRoleRes = await this.loginService.getUserRoles(userName);
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);

      const isCustomIDPAgent = this.PROFILE === environment.ON_CUSTOMIDP && this.loginService.isAgentUser();

      if (isCustomIDPAgent) {
        console.warn('Agent profile fetch failed, trying fallback');
        try {
          userRoleRes = await this.loginService.getUserRoles(userName);
        } catch (fallbackError) {
          console.error('Fallback also failed:', fallbackError);
          userRoleRes = {};
        }
      } else {
        userRoleRes = {};
      }
    }
    this.processUserRoleData(userRoleRes);
    await this.commonService.getAllGroupNames();

    let profiles;
    const isCustomIDPAgent = this.PROFILE === environment.ON_CUSTOMIDP && this.loginService.isAgentUser();

    if (isCustomIDPAgent && userRoleRes['_links']?.item?.[0]?.summary) {
      profiles = userRoleRes['_links'].item[0].summary;

      if (!profiles.uuid) {
        if (userRoleRes['_links']?.item?.[0]?.href) {
          const hrefParts = userRoleRes['_links'].item[0].href.split('/');
          profiles.uuid = hrefParts[hrefParts.length - 1];
        } else if (profiles.id) {
          profiles.uuid = profiles.id;
        } else if (profiles.profileId) {
          profiles.uuid = profiles.profileId;
        }
      }

      if (!profiles.uuid) {
        console.warn('Unable to extract profileId for agent, available fields:', Object.keys(profiles));
      }
    } else {
      try {
        profiles = await this.loginService.getActiveProfile(userName);
      } catch (error) {
        console.error(error);
        profiles = {};
      }
    }
    this.processProfileData(profiles, userRoleRes['groupId'], init);

    this.loginScreen();
  }

  async initLogin() {
    localStorage.setItem('isSignUp', 'N');
    this.navigateSelect();
    this.environmentURLSetup();
    if (this.PROFILE !== environment.ON_CUSTOMIDP) {
      if (sessionStorage.getItem('isLogin_Group') !== 'yes') {
        const path = this.location.path();
        if (path === '' || path === '/') {
          sessionStorage.removeItem('isLogin');
          localStorage.removeItem('userName');
        }
      }
    }
    // OIDC enviroment (OKTA,o Auth0, etc....)
    if (this.ON_PREMISES === this.PROFILE) {
      this.onOIDCEnviroment();
    }
    // Congito environment
    else if (this.ON_CLOUD === this.PROFILE) {
      this.onCognitoEnvironment();
    }
    // CAS Environment
    else if (this.PROFILE === this.ON_PREMISES_CAS) {
      this.onCASEnvironment();
    } else if (this.PROFILE === environment.ON_CUSTOMIDP) {
      this.onCustomIDPEnvironment();
    }
  }

  // OIDC enviroment (OKTA,o Auth0, etc....)
  onOIDCEnviroment() {
    if (sessionStorage.getItem('nonce')) {
      this.isAuthorized = true;
      this.hasIdtoken = this.oauthService.hasValidIdToken();
      this.hasAccessToken = this.oauthService.hasValidAccessToken();
      this.loginScreen();
      setTimeout(() => {
        this.commonService.get_tokenInfo();
        this.commonService.getUserInfo(localStorage.getItem('userName'));
      }, 3000);
    } else {
      this.oauthService.initLoginFlow();
    }
    this.setLocalStorageWithAPI();
  }

  // Congito environment
  onCognitoEnvironment() {
    if (sessionStorage.getItem('isLogin') !== 'yes') {
      this.loginService.redirectToLogin();
    } else {
      this.isAuthorized = true;
      this.accessToken = localStorage.getItem('access_token');
      this.userId = localStorage.getItem('userName');
      this.activatedRoute.queryParams.subscribe(async (params) => {
        if (params['code']) {
          if (!localStorage.getItem('access_token') || !localStorage.getItem('userName')) {
            //getting access token
            await this.loginService.getAccessToken(params['code']);
            let tokenInfo = this.loginService.get_tokenInfo();
            this.userId = tokenInfo.identities[0].userId;
            await this.initParam(this.userId);
            this.setLocalStorageWithAPI();
          }
        } else if (this.accessToken != '' && this.accessToken != null && this.accessToken != undefined) {
          if (!localStorage.getItem('userName')) {
            await this.initParam(this.userId);
          }
          this.setLocalStorageWithAPI();
        }
      });
    }
  }

  // CAS Environment
  onCASEnvironment() {
    if (sessionStorage.getItem('isLogin') !== 'yes') {
      this.loginService.CAS_LoginPage();
      return;
    }
    this.isAuthorized = true;
    this.accessToken = localStorage.getItem('access_token');
    this.userId = localStorage.getItem('userName');
    this.activatedRoute.queryParams.subscribe(async (params) => {
      let paramsCode = params['code'];
      try {
        if (paramsCode) {
          if (!localStorage.getItem('access_token') || !localStorage.getItem('userName')) {
            const res = await this.loginService.getToken_CAS(paramsCode);
            const token = res.access_token;
            localStorage.setItem('access_token', token);

            const tokenInfo = this.loginService.get_tokenInfo();
            this.userId = tokenInfo.sub;
            await this.initParam(this.userId);
            this.setLocalStorageWithAPI();
          }
        } else if (!this.rbacService.getUser() && localStorage.getItem('userName')) {
          await this.initParam(localStorage.getItem('userName'), false);
        }
      } catch (error) {
        console.error(error);
      }
    });
    this.loginScreen();
  }

  async onCustomIDPEnvironment() {
    const urlParams = new URLSearchParams(window.location.search);
    const authCode = urlParams.get('authCode');

    if (authCode) {
      return;
    }

    await this.initializeCustomIDPAfterAuth();
  }

  async initializeCustomIDPAfterAuth(): Promise<void> {
    const userName = localStorage.getItem('userName') ?? this.cookieService.get('username');

    if (!userName) {
      return;
    }

    sessionStorage.setItem('isLogin', 'yes');
    this.isAuthorized = true;

    this.userId = userName;

    this.commonService.initModuleSerivce();

    await this.initParam(this.userId, userName ? false : true);

    try {
      await this.setLocalStorageWithAPI();
    } catch (error) {
      console.error(error);
    }

    this.cdr.detectChanges();

    setTimeout(() => {
      const url = new URL(window.location.href);
      if (url.searchParams.has('authCode')) {
        url.searchParams.delete('authCode');
        const newPath = url.pathname + (url.search || '') + (url.hash || '');
        this.location.replaceState(newPath);
      }
    }, 50);
  }

  // hard code for dev
  async setLocalStorage() {
    sessionStorage.setItem('isLogin', 'yes');
    localStorage.setItem('isLogin', 'yes');
    localStorage.setItem('isSignUp', 'N');
    this.initParam('groupSales12345@dxc.com', false);
  }

  sideBarToggler() {
    this.isMenuOpend = !this.isMenuOpend;
  }

  navigateToDetails(event: any) {
    if (this.isMobile) {
      this.isMenuOpend = false;
    }
    this.router.navigate([event.path], { relativeTo: this.activatedRoute });
    this.navigateSelect();
  }

  redirectHome(event) {
    this.navigateSelect();
  }

  navigateSelect(): void {
    const currentUrl = this.router.url;

    this.navigationMenu.forEach((menu) => {
      menu.isSelected = currentUrl.includes(menu.path);
    });
  }
}
